import { useCallback, useEffect, useRef, useState } from "react";
import { palette } from "./pageStyles";

/** Keeps a panel size inside its limits. Pure, so it is unit-tested. */
export function clampSize(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Reads saved sizes, keeping only known keys with numeric values. */
export function readSavedSizes(raw, defaults) {
  let saved = {};
  try {
    saved = raw ? JSON.parse(raw) : {};
  } catch {
    saved = {};
  }
  const out = { ...defaults };
  Object.keys(defaults).forEach((key) => {
    if (Number.isFinite(saved?.[key])) out[key] = saved[key];
  });
  return out;
}

/** Panel sizes in px, remembered in this browser between visits. */
export function usePanelSizes(storageKey, defaults) {
  const [sizes, setSizes] = useState(() => {
    try {
      return readSavedSizes(window.localStorage.getItem(storageKey), defaults);
    } catch {
      return { ...defaults };
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(sizes));
    } catch {
      // storage blocked: sizes still work for this visit
    }
  }, [sizes, storageKey]);
  const setSize = useCallback((key, value) => setSizes((prev) => ({ ...prev, [key]: value })), []);
  const reset = useCallback(() => setSizes({ ...defaults }), [defaults]);
  return [sizes, setSize, reset];
}

/**
 * Drag bar between two panels. orientation "vertical" = a bar between columns (drag left/right),
 * "horizontal" = a bar between stacked panels (drag up/down). `invert` for panels sized from the
 * other side (e.g. a right-hand column grows when dragged left). Arrow keys move it 16 px.
 */
export function ResizeHandle({ orientation = "vertical", label, value, min, max, invert = false, onChange, onReset }) {
  const start = useRef(null);
  const [active, setActive] = useState(false);
  const vertical = orientation === "vertical";

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start.current = { pos: vertical ? e.clientX : e.clientY, value };
    setActive(true);
  };
  const onPointerMove = (e) => {
    if (!start.current) return;
    const delta = (vertical ? e.clientX : e.clientY) - start.current.pos;
    onChange(clampSize(start.current.value + (invert ? -delta : delta), min, max));
  };
  const onPointerUp = () => {
    start.current = null;
    setActive(false);
  };
  const onKeyDown = (e) => {
    const grow = vertical ? (invert ? "ArrowLeft" : "ArrowRight") : invert ? "ArrowUp" : "ArrowDown";
    const shrink = vertical ? (invert ? "ArrowRight" : "ArrowLeft") : invert ? "ArrowDown" : "ArrowUp";
    if (e.key === grow) onChange(clampSize(value + 16, min, max));
    else if (e.key === shrink) onChange(clampSize(value - 16, min, max));
    else if (e.key === "Home") onChange(min);
    else if (e.key === "End") onChange(max);
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={vertical ? "vertical" : "horizontal"}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      title={`${label}. Drag, or use the arrow keys. Double-click to reset.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
      className="lwt-resize-handle"
      style={{
        position: "relative",
        flex: "0 0 auto",
        [vertical ? "width" : "height"]: "7px",
        [vertical ? "margin" : "margin"]: vertical ? "0 -3px" : "-3px 0",
        zIndex: 5,
        cursor: vertical ? "col-resize" : "row-resize",
        touchAction: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: vertical ? "row" : "column",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          [vertical ? "width" : "height"]: active ? "3px" : "1px",
          [vertical ? "height" : "width"]: "100%",
          background: active ? palette.text : palette.border,
        }}
      />
    </div>
  );
}

/** Square icon button that puts a tool in full screen and back. */
export function FullscreenButton({ active, onToggle, label }) {
  const text = active ? `Exit full screen (${label})` : `Full screen (${label})`;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label={text}
      title={active ? "Exit full screen (Esc)" : "Full screen"}
      style={{
        width: 36,
        height: 36,
        flex: "0 0 auto",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: 4,
        background: active ? palette.selected : "transparent",
        color: palette.text,
        cursor: "pointer",
      }}
    >
      {active ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
        </svg>
      )}
    </button>
  );
}

/** Style for a tool while it is in full screen: covers the whole window. */
export const fullscreenStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  display: "flex",
  flexDirection: "column",
  background: palette.background,
  width: "auto",
  maxWidth: "none",
  height: "auto",
  maxHeight: "none",
};
